import { GridContainer, Select } from '@bob/library'
import { trackLink } from '@websrc/App/Helpers/adobeTracking'
import { pathToPageName } from '@websrc/App/Helpers/pathToPageName'
import { spotWrapperProps } from '@websrc/App/Helpers/spotWrapperProps'
import { usePath } from '@websrc/App/Hooks/usePath'
import { useStaticText } from '@websrc/App/Hooks/useStaticText'
import { mapCurriculum } from '@websrc/DataMappers/mapSpot'
import React, { ChangeEvent } from 'react'

import { LearnMoreButtonType } from '../Collapse/SpotCollapse'
import { SpotCurriculumItem } from './Item/SpotCurriculumItem'
import * as SC from './SpotCurriculum.style'
import { usePlatformsLanguagesCurriculum } from './usePlatformsLanguagesCurriculum'

export interface SpotCurriculumProps {
  spot: ReturnType<typeof mapCurriculum>['curriculum']
  LearnMoreButton?: LearnMoreButtonType
  imageLazy?: boolean
}

export function SpotCurriculum({ LearnMoreButton, spot, imageLazy }: SpotCurriculumProps) {
  const path = usePath()
  let [, pageName] = pathToPageName(path)
  pageName = pageName.slice('legoeducation:'.length)

  const t = useStaticText()

  const [
    filteredPlatforms,
    filteredLanguages,
    filteredCurriculum,
    selectedPlatformI,
    setSelectedPlatformI,
    selectedLangI,
    setSelectedLangI,
  ] = usePlatformsLanguagesCurriculum(spot)

  const handleSelectPlatform = (e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedPlatformI(parseInt(e.target.value, 10))
    trackLink({ name: `curriculum:change-platform:${pageName}` }, 'o')
  }

  const handleSelectLanguage = (e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedLangI(parseInt(e.target.value, 10))
    trackLink({ name: `curriculum:change-language:${pageName}` }, 'o')
  }

  return (
    <GridContainer>
      <SC.Wrapper desktop={10} {...spotWrapperProps(spot.caption)}>
        <SC.Title>{spot.headline}</SC.Title>
        {spot.description && <SC.Description>{spot.description}</SC.Description>}
        <SC.SelectorWrapper>
          <SC.SelectWithLabel>
            <SC.SelectLabel>{t.downloads.choose_platform}</SC.SelectLabel>
            <Select onChange={handleSelectPlatform} value={`${selectedPlatformI}`}>
              {filteredPlatforms.map((os, i) => (
                <option key={i} value={`${i}`}>
                  {os === 'All devices' ? t.downloads.all_devices : os}
                </option>
              ))}
            </Select>
          </SC.SelectWithLabel>

          <SC.SelectWithLabel>
            <SC.SelectLabel>{t.downloads.choose_language}</SC.SelectLabel>
            <Select onChange={handleSelectLanguage} value={`${selectedLangI}`}>
              {filteredLanguages.map((l, i) => (
                <option key={i} value={`${i}`}>
                  {l}
                </option>
              ))}
            </Select>
          </SC.SelectWithLabel>
        </SC.SelectorWrapper>
        <SC.Items>
          {filteredCurriculum.map((curriculum, i) => (
            <SpotCurriculumItem
              item={curriculum}
              key={i}
              lang={filteredLanguages[selectedLangI]}
              lazyLoadImage={imageLazy}
              platform={filteredPlatforms[selectedPlatformI]}
            />
          ))}
        </SC.Items>

        {LearnMoreButton && (
          <SC.LearnMoreButtonWrapper>
            <LearnMoreButton />
          </SC.LearnMoreButtonWrapper>
        )}
      </SC.Wrapper>
    </GridContainer>
  )
}
